import { useEffect, useState, useCallback, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  FileText, Plus, RefreshCw, Mail, CheckCircle2,
  Clock, Bell, Eye, X as XIcon, Send, ShieldCheck,
  History, ChevronDown, ChevronUp,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Mapping van offerte-status naar voorgestelde mailtype voor preview.
// Wordt door de admin-UI gebruikt om de juiste preview-knop te kiezen.
function suggestedMailType(quote) {
  if (quote.reminder_due) return 'quote_waiting_customer_reminder';
  if (quote.status === 'approved') return 'quote_approved_confirmation';
  if (quote.status === 'rejected') return 'quote_rejected_confirmation';
  if (['waiting_customer', 'quote_received'].includes(quote.status))
    return 'quote_available';
  return null;
}

function mailButtonLabel(quote) {
  if (quote.reminder_due) return 'Bekijk herinneringsmail';
  if (quote.status === 'approved' || quote.status === 'rejected')
    return 'Bekijk bevestigingsmail';
  return 'Bekijk mailvoorbeeld';
}

const STATUS_LABEL = {
  requested: 'Aangevraagd',
  in_review: 'In behandeling',
  quote_received: 'Offerte ontvangen',
  waiting_customer: 'Wacht op klant',
  approved: 'Goedgekeurd',
  rejected: 'Afgewezen',
  expired: 'Verlopen',
  cancelled: 'Geannuleerd',
};

const STATUS_TONE = {
  waiting_customer: 'bg-amber-100 text-amber-900 border-amber-200',
  approved: 'bg-emerald-100 text-emerald-900 border-emerald-200',
  rejected: 'bg-rose-100 text-rose-900 border-rose-200',
  expired: 'bg-slate-100 text-slate-700 border-slate-200',
  in_review: 'bg-blue-50 text-blue-900 border-blue-200',
  requested: 'bg-blue-50 text-blue-900 border-blue-200',
  quote_received: 'bg-sky-50 text-sky-900 border-sky-200',
  cancelled: 'bg-slate-100 text-slate-700 border-slate-200',
};

// Statuses an admin may set. We intentionally exclude `approved`/`rejected`
// from the admin status dropdown to prevent silently overwriting a customer
// decision. Admin can still set them via API but we won't expose a 1-click
// path; a confirm dialog gates it.
const ADMIN_SETTABLE = [
  'requested', 'in_review', 'quote_received', 'waiting_customer',
  'expired', 'cancelled',
];

// Mail activation flag — switched on in Blok D when Resend goes live.
// Until then the "Echte mail versturen"-knop blijft disabled in QuoteRow.
// eslint-disable-next-line no-unused-vars
const MAIL_ACTIVE = false;

function fmtAmount(q) {
  if (q.amount_cents != null) {
    try {
      return new Intl.NumberFormat('nl-NL', {
        style: 'currency', currency: q.currency || 'EUR',
      }).format(q.amount_cents / 100);
    } catch { return `€ ${(q.amount_cents / 100).toFixed(2)}`; }
  }
  return q.amount_label || '—';
}

function fmtDateTime(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('nl-NL', {
      day: '2-digit', month: 'short', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  } catch { return iso; }
}

function StatusPill({ status }) {
  const tone = STATUS_TONE[status] || 'bg-slate-100 text-slate-700 border-slate-200';
  return (
    <span className={`text-[10px] px-2 py-0.5 rounded-full border whitespace-nowrap ${tone}`}
      data-testid={`admin-quote-status-${status}`}>
      {STATUS_LABEL[status] || status}
    </span>
  );
}

function KpiCard({ label, value, testid, tone = 'slate' }) {
  const toneClass = {
    slate: 'text-slate-900', amber: 'text-amber-700',
    emerald: 'text-emerald-700', rose: 'text-rose-700',
    blue: 'text-blue-700', sky: 'text-sky-700',
  }[tone];
  return (
    <div className="bg-white border border-border rounded-xl p-3" data-testid={testid}>
      <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</div>
      <div className={`text-2xl font-semibold mt-1 ${toneClass}`}>{value}</div>
    </div>
  );
}

function CreateForm({ token, onCreated, onClose, pilots }) {
  const [form, setForm] = useState({
    title: '', summary: '', description: '',
    installer_name: '', amount_cents: '', amount_label: '',
    valid_until: '', subject_id: '', subject_kind: 'pilot',
    custom_subject: '',
  });
  const [busy, setBusy] = useState(false);

  const subjectIdFinal = () => {
    if (form.subject_kind === 'pilot' && form.subject_id) {
      return `pilot:${form.subject_id}`;
    }
    if (form.subject_kind === 'custom' && form.custom_subject.trim()) {
      return form.custom_subject.trim();
    }
    return null;
  };

  const submit = async () => {
    if (!form.title.trim() || !form.summary.trim()) {
      toast.error('Titel en korte omschrijving zijn vereist');
      return;
    }
    const sid = subjectIdFinal();
    if (!sid) { toast.error('Kies een pilot of vul een ID in'); return; }
    setBusy(true);
    try {
      const body = {
        title: form.title.trim(),
        summary: form.summary.trim(),
        description: form.description.trim() || null,
        installer_name: form.installer_name.trim() || null,
        amount_cents: form.amount_cents ? Math.round(Number(form.amount_cents) * 100) : null,
        amount_label: form.amount_label.trim() || null,
        valid_until: form.valid_until || null,
        subject_id: sid,
        is_test_quote: true,
      };
      const r = await axios.post(`${API}/admin/quotes/create-test`, body, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success('Test-offerte aangemaakt');
      onCreated(r.data?.quote);
    } catch (e) {
      toast.error(e?.response?.data?.detail?.error || e?.response?.data?.detail || e.message);
    } finally { setBusy(false); }
  };

  return (
    <div className="bg-white border border-border rounded-xl p-4 space-y-3"
      data-testid="admin-quote-create-form">
      <div className="flex items-center justify-between">
        <h3 className="font-semibold">Nieuwe test-offerte</h3>
        <button onClick={onClose} className="text-xs text-muted-foreground hover:text-foreground"
          data-testid="admin-quote-create-close">Sluiten</button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <label className="text-sm space-y-1">
          <span className="text-xs text-muted-foreground">Type ontvanger</span>
          <select value={form.subject_kind}
            onChange={(e) => setForm(f => ({ ...f, subject_kind: e.target.value }))}
            className="w-full border border-border rounded-md p-2 text-sm bg-white"
            data-testid="admin-quote-create-subject-kind">
            <option value="pilot">Pilot</option>
            <option value="custom">Handmatig ID (geavanceerd)</option>
          </select>
        </label>

        {form.subject_kind === 'pilot' && (
          <label className="text-sm space-y-1">
            <span className="text-xs text-muted-foreground">Kies pilot</span>
            <select value={form.subject_id}
              onChange={(e) => setForm(f => ({ ...f, subject_id: e.target.value }))}
              className="w-full border border-border rounded-md p-2 text-sm bg-white"
              data-testid="admin-quote-create-pilot-select">
              <option value="">— kies pilot —</option>
              {pilots.map(p => (
                <option key={p.pilot_id} value={p.pilot_id}>
                  {p.name || 'Pilot'} {p.pilot_type ? `(${p.pilot_type})` : ''} · {p.pilot_id.slice(-6)}
                </option>
              ))}
            </select>
          </label>
        )}

        {form.subject_kind === 'custom' && (
          <label className="text-sm space-y-1">
            <span className="text-xs text-muted-foreground">Subject ID (pilot:&lt;id&gt; of user_id)</span>
            <input value={form.custom_subject}
              onChange={(e) => setForm(f => ({ ...f, custom_subject: e.target.value }))}
              placeholder="pilot:abc123 of user_id"
              className="w-full border border-border rounded-md p-2 text-sm"
              data-testid="admin-quote-create-custom-subject" />
          </label>
        )}

        <label className="text-sm space-y-1 md:col-span-2">
          <span className="text-xs text-muted-foreground">Titel *</span>
          <input value={form.title}
            onChange={(e) => setForm(f => ({ ...f, title: e.target.value }))}
            placeholder="Bijv. Installatie warmtepomp"
            className="w-full border border-border rounded-md p-2 text-sm"
            data-testid="admin-quote-create-title" />
        </label>

        <label className="text-sm space-y-1 md:col-span-2">
          <span className="text-xs text-muted-foreground">Korte omschrijving *</span>
          <input value={form.summary}
            onChange={(e) => setForm(f => ({ ...f, summary: e.target.value }))}
            placeholder="Bijv. Levering + montage + service 1 jaar"
            className="w-full border border-border rounded-md p-2 text-sm"
            data-testid="admin-quote-create-summary" />
        </label>

        <label className="text-sm space-y-1 md:col-span-2">
          <span className="text-xs text-muted-foreground">Uitgebreide beschrijving (optioneel)</span>
          <textarea value={form.description}
            onChange={(e) => setForm(f => ({ ...f, description: e.target.value }))}
            rows={3}
            className="w-full border border-border rounded-md p-2 text-sm"
            data-testid="admin-quote-create-description" />
        </label>

        <label className="text-sm space-y-1">
          <span className="text-xs text-muted-foreground">Bedrag (EUR, optioneel)</span>
          <input value={form.amount_cents} type="number" step="0.01" min="0"
            onChange={(e) => setForm(f => ({ ...f, amount_cents: e.target.value }))}
            placeholder="1845.00"
            className="w-full border border-border rounded-md p-2 text-sm"
            data-testid="admin-quote-create-amount" />
        </label>

        <label className="text-sm space-y-1">
          <span className="text-xs text-muted-foreground">Aanbieder/installateur (optioneel)</span>
          <input value={form.installer_name}
            onChange={(e) => setForm(f => ({ ...f, installer_name: e.target.value }))}
            placeholder="Bijv. Powersure Test BV"
            className="w-full border border-border rounded-md p-2 text-sm"
            data-testid="admin-quote-create-installer" />
        </label>

        <label className="text-sm space-y-1">
          <span className="text-xs text-muted-foreground">Geldig tot (optioneel)</span>
          <input value={form.valid_until} type="date"
            onChange={(e) => setForm(f => ({ ...f, valid_until: e.target.value }))}
            className="w-full border border-border rounded-md p-2 text-sm"
            data-testid="admin-quote-create-valid-until" />
        </label>

        <label className="text-sm space-y-1">
          <span className="text-xs text-muted-foreground">Bedrag-label (als geen bedrag)</span>
          <input value={form.amount_label}
            onChange={(e) => setForm(f => ({ ...f, amount_label: e.target.value }))}
            placeholder="Bijv. Prijs op aanvraag"
            className="w-full border border-border rounded-md p-2 text-sm"
            data-testid="admin-quote-create-amount-label" />
        </label>
      </div>

      <div className="flex items-center gap-2 pt-1">
        <span className="text-[10px] px-2 py-0.5 rounded-full bg-blue-50 text-blue-800 border border-blue-200">
          Wordt opgeslagen als test-offerte
        </span>
        <span className="text-[10px] text-muted-foreground">
          (status = &quot;Wacht op klant&quot;, géén mail, géén provider, géén Stripe)
        </span>
      </div>

      <div className="flex gap-2">
        <button onClick={submit} disabled={busy}
          className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-md text-sm font-medium disabled:opacity-50"
          data-testid="admin-quote-create-submit">
          {busy ? 'Bezig…' : 'Test-offerte aanmaken'}
        </button>
        <button onClick={onClose} className="px-4 py-2 bg-white border border-border rounded-md text-sm">
          Annuleren
        </button>
      </div>
    </div>
  );
}

function QuoteRow({ quote, token, onChanged, onPreview }) {
  const [busy, setBusy] = useState(false);
  const [mailEventTick, setMailEventTick] = useState(0);
  const isLocked = ['approved', 'rejected'].includes(quote.status);

  const setStatus = async (next) => {
    if (next === quote.status) return;
    if (isLocked) {
      const ok = window.confirm(
        `Klant heeft al "${STATUS_LABEL[quote.status]}".\n\n` +
        `Status wijzigen overschrijft de klantbeslissing. Doorgaan?`
      );
      if (!ok) return;
    }
    setBusy(true);
    try {
      const r = await axios.post(
        `${API}/admin/quotes/${quote.quote_id}/status`,
        { status: next },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      toast.success('Status bijgewerkt');
      onChanged(r.data?.quote);
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
    } finally { setBusy(false); }
  };

  return (
    <tr className="border-b border-border last:border-0 align-top"
      data-testid={`admin-quote-row-${quote.quote_id}`}>
      <td className="py-3 pr-3">
        <div className="font-medium text-sm">{quote.title}</div>
        {quote.installer_name && (
          <div className="text-[11px] text-muted-foreground">via {quote.installer_name}</div>
        )}
        <div className="text-[11px] text-muted-foreground mt-0.5 break-all">
          {quote.subject_id || '—'}
        </div>
        {quote.is_test_quote && (
          <span className="inline-block mt-1 text-[10px] px-1.5 py-0.5 rounded bg-blue-50 text-blue-800 border border-blue-200"
            data-testid="admin-quote-test-badge">
            test
          </span>
        )}
      </td>
      <td className="py-3 pr-3 text-sm whitespace-nowrap">{fmtAmount(quote)}</td>
      <td className="py-3 pr-3">
        <div className="flex items-center gap-1.5 flex-wrap">
          <StatusPill status={quote.status} />
          {quote.reminder_due && (
            <span className="inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full bg-amber-50 text-amber-800 border border-amber-200"
              data-testid="admin-quote-reminder-badge">
              <Bell className="w-3 h-3" /> Reminder nodig
            </span>
          )}
          {quote.status === 'waiting_customer' && !quote.reminder_due && (
            <span className="text-[10px] text-muted-foreground">
              &lt; 3 dagen
            </span>
          )}
        </div>
      </td>
      <td className="py-3 pr-3 text-[11px] text-muted-foreground">
        <div>Gemaakt: {fmtDateTime(quote.created_at)}</div>
        <div>Gewijzigd: {fmtDateTime(quote.updated_at)}</div>
        {quote.customer_decision_at && (
          <div className="text-emerald-700">Beslissing: {fmtDateTime(quote.customer_decision_at)}</div>
        )}
        {quote.customer_decision_note && (
          <div>Reden: <em>{quote.customer_decision_note}</em></div>
        )}
      </td>
      <td className="py-3 pr-3">
        <div className="flex flex-col gap-1.5">
          <select value={quote.status} disabled={busy}
            onChange={(e) => setStatus(e.target.value)}
            className="border border-border rounded-md p-1.5 text-xs bg-white min-w-[12rem]"
            data-testid={`admin-quote-status-select-${quote.quote_id}`}>
            {ADMIN_SETTABLE.map(s => (
              <option key={s} value={s}>{STATUS_LABEL[s]}</option>
            ))}
            {isLocked && (
              <option value={quote.status}>
                {STATUS_LABEL[quote.status]} (klantbeslissing)
              </option>
            )}
          </select>

          <button
            type="button"
            onClick={() => onPreview(quote)}
            title="Bekijk welke mail er VERSTUURD ZOU WORDEN als Blok D actief is. Verstuurt niets."
            className="inline-flex items-center justify-center gap-1.5 px-2 py-1.5 rounded-md text-xs bg-white border border-border hover:border-emerald-300 hover:bg-emerald-50 transition-colors"
            data-testid={`admin-quote-preview-mail-${quote.quote_id}`}
          >
            <Eye className="w-3 h-3" /> {mailButtonLabel(quote)}
          </button>

          <SendMailButton quote={quote} token={token}
            onMailSent={() => setMailEventTick(t => t + 1)} />
          <MailTimelinePanel quote={quote} token={token}
            refreshTrigger={mailEventTick} />
        </div>
      </td>
    </tr>
  );
}

// ---------- Verstuur mail-knop + result indicator + mini-timeline ---------
function suggestedSendType(quote) {
  if (quote.reminder_due) return 'quote_waiting_customer_reminder';
  if (quote.status === 'approved') return 'quote_approved_confirmation';
  if (quote.status === 'rejected') return 'quote_rejected_confirmation';
  if (['waiting_customer', 'quote_received'].includes(quote.status))
    return 'quote_available';
  return null;
}

const RESULT_TONE = {
  live_send: 'bg-emerald-50 text-emerald-900 border-emerald-200',
  dry_run: 'bg-amber-50 text-amber-900 border-amber-200',
  disabled: 'bg-slate-50 text-slate-700 border-slate-200',
  duplicate_blocked: 'bg-blue-50 text-blue-900 border-blue-200',
  not_allowlisted: 'bg-rose-50 text-rose-900 border-rose-200',
  invalid_for_status: 'bg-slate-50 text-slate-700 border-slate-200',
  no_recipient: 'bg-rose-50 text-rose-900 border-rose-200',
};

function SendMailButton({ quote, token, onMailSent }) {
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const sendType = suggestedSendType(quote);
  const disabled = !sendType || busy;

  const onClick = async () => {
    if (!sendType) return;
    const ok = window.confirm(
      `Wilt u nu een ${sendType === 'quote_waiting_customer_reminder'
        ? 'herinneringsmail' : 'mail'} versturen voor deze offerte?\n\n` +
      `In de huidige preview-omgeving wordt dit een dry-run — er gaat ` +
      `geen echte mail uit. Op productie geldt MAIL_ALLOWLIST + per-` +
      `mailtype activatie.`
    );
    if (!ok) return;
    setBusy(true);
    try {
      const r = await axios.post(
        `${API}/admin/quotes/${quote.quote_id}/send-mail`,
        { mail_type: sendType, note: 'admin-ui' },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setResult(r.data);
      toast.success(r.data?.result_label || 'Actie uitgevoerd');
      if (typeof onMailSent === 'function') onMailSent(r.data);
    } catch (e) {
      const msg = e?.response?.data?.detail?.error
        || e?.response?.data?.detail || e.message;
      toast.error(typeof msg === 'string' ? msg : 'Versturen mislukt');
    } finally { setBusy(false); }
  };

  return (
    <>
      <button
        type="button"
        onClick={onClick}
        disabled={disabled}
        title={sendType
          ? 'Verstuur via dispatcher (dry-run in preview)'
          : 'Niet beschikbaar voor deze status'}
        className={`inline-flex items-center justify-center gap-1.5 px-2 py-1.5 rounded-md text-xs border transition-colors ${
          disabled
            ? 'bg-slate-100 text-slate-500 border-border cursor-not-allowed'
            : 'bg-emerald-600 text-white border-emerald-600 hover:bg-emerald-700'
        }`}
        data-testid={`admin-quote-send-mail-${quote.quote_id}`}
      >
        <Send className="w-3 h-3" /> {busy ? 'Bezig…' : 'Verstuur mail'}
      </button>
      {result && (
        <div
          className={`text-[11px] px-2 py-1 rounded-md border ${
            RESULT_TONE[result.action] || 'bg-slate-50 border-border'
          }`}
          data-testid={`admin-quote-send-result-${quote.quote_id}`}
        >
          <div className="font-medium flex items-center gap-1">
            <ShieldCheck className="w-3 h-3" />
            {result.result_label || result.action}
          </div>
          {result.action !== 'live_send' && (
            <div className="text-[10px] opacity-80 mt-0.5">
              Geen mail verzonden · dispatcher-veiligheid actief
            </div>
          )}
          {result.action === 'live_send' && (
            <div className="text-[10px] opacity-80 mt-0.5">
              Status: {result.send_status || '—'}
            </div>
          )}
        </div>
      )}
    </>
  );
}

const FILTERS = [
  { id: 'all',      label: 'Alle' },
  { id: 'waiting',  label: 'Wacht op klant' },
  { id: 'approved', label: 'Goedgekeurd' },
  { id: 'rejected', label: 'Afgewezen' },
  { id: 'reminder', label: 'Reminder nodig' },
  { id: 'test',     label: 'Test-offertes' },
];

// ---------- Mail-timeline paneel (lazy fetch on first open) ---------------
const TIMELINE_TONE = {
  'Verzonden': 'text-emerald-700',
  'Dry-run': 'text-amber-700',
  'Niet geactiveerd': 'text-slate-600',
  'Geblokkeerd (eerder verzonden)': 'text-blue-700',
  'Ontvanger niet op allowlist': 'text-rose-700',
  'Mislukt': 'text-rose-700',
  'Tijdelijk geblokkeerd': 'text-rose-700',
  'Quota overschreden': 'text-rose-700',
  'Domein niet geverifieerd': 'text-rose-700',
};

function fmtTimelineDate(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('nl-NL', {
      day: '2-digit', month: 'short',
      hour: '2-digit', minute: '2-digit',
    });
  } catch { return iso; }
}

function MailTimelinePanel({ quote, token, refreshTrigger }) {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const fetchTimeline = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const r = await axios.get(
        `${API}/admin/quotes/${quote.quote_id}/mail-timeline`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setItems(r.data?.items || []);
    } catch {
      setError('Kon mailhistorie niet laden');
      setItems([]);
    } finally { setLoading(false); }
  }, [quote.quote_id, token]);

  useEffect(() => {
    if (open) fetchTimeline();
  }, [open, refreshTrigger, fetchTimeline]);

  return (
    <div className="w-full">
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className="inline-flex items-center gap-1 text-[11px] text-muted-foreground hover:text-foreground transition-colors"
        data-testid={`admin-quote-timeline-toggle-${quote.quote_id}`}
        aria-expanded={open}
      >
        <History className="w-3 h-3" />
        {open ? 'Verberg mailhistorie' : 'Bekijk mailhistorie'}
        {open ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
      </button>

      {open && (
        <div
          className="mt-1.5 bg-slate-50 border border-border rounded-md p-2 text-[11px]"
          data-testid={`admin-quote-timeline-panel-${quote.quote_id}`}
        >
          {loading && (
            <div className="text-muted-foreground text-center py-2">Laden…</div>
          )}
          {!loading && error && (
            <div className="text-rose-700">{error}</div>
          )}
          {!loading && !error && items && items.length === 0 && (
            <div className="text-muted-foreground italic"
              data-testid={`admin-quote-timeline-empty-${quote.quote_id}`}>
              Er zijn nog geen mailpogingen voor deze offerte.
            </div>
          )}
          {!loading && !error && items && items.length > 0 && (
            <ul className="space-y-1.5" data-testid={`admin-quote-timeline-list-${quote.quote_id}`}>
              {items.slice(0, 5).map((it, idx) => (
                <li key={`${it.created_at}-${idx}`}
                  className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
                  <span className="font-mono text-[10px] text-muted-foreground whitespace-nowrap">
                    {fmtTimelineDate(it.created_at)}
                  </span>
                  <span className={`font-medium ${TIMELINE_TONE[it.status_label] || 'text-foreground'}`}>
                    {it.status_label}
                  </span>
                  <span className="text-muted-foreground">·</span>
                  <span className="text-foreground">{it.mail_type_label}</span>
                  {it.to_masked && (
                    <>
                      <span className="text-muted-foreground">·</span>
                      <span className="text-muted-foreground truncate max-w-[180px]">
                        {it.to_masked}
                      </span>
                    </>
                  )}
                </li>
              ))}
              {items.length > 5 && (
                <li className="text-[10px] text-muted-foreground italic">
                  + {items.length - 5} eerdere pogingen niet getoond
                </li>
              )}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}


export default function AdminQuotesTab({ token }) {
  const H = useMemo(() => ({ headers: { Authorization: `Bearer ${token}` } }), [token]);
  const [items, setItems] = useState([]);
  const [counts, setCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [showForm, setShowForm] = useState(false);
  const [pilots, setPilots] = useState([]);
  const [previewQuote, setPreviewQuote] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [q, p] = await Promise.all([
        axios.get(`${API}/admin/quotes`, H),
        axios.get(`${API}/admin/pilot-devices`, H).catch(() => ({ data: { items: [] } })),
      ]);
      setItems(q.data?.items || []);
      setCounts(q.data?.counts || {});
      setPilots(p.data?.items || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
    } finally { setLoading(false); }
  }, [H]);

  useEffect(() => { load(); }, [load]);

  const onCreated = (q) => {
    setShowForm(false);
    if (q) setItems(prev => [q, ...prev]);
    // refresh counts
    load();
  };

  const onChanged = (q) => {
    if (!q) return;
    setItems(prev => prev.map(x => x.quote_id === q.quote_id ? q : x));
    load();
  };

  const filtered = items.filter(q => {
    if (filter === 'all') return true;
    if (filter === 'waiting') return q.status === 'waiting_customer';
    if (filter === 'approved') return q.status === 'approved';
    if (filter === 'rejected') return q.status === 'rejected';
    if (filter === 'reminder') return q.reminder_due === true;
    if (filter === 'test') return q.is_test_quote === true;
    return true;
  });

  const kTotal     = items.length;
  const kWaiting   = counts.waiting_customer || 0;
  const kApproved  = counts.approved || 0;
  const kRejected  = counts.rejected || 0;
  const kReminder  = items.filter(q => q.reminder_due === true).length;
  const kTest      = items.filter(q => q.is_test_quote === true).length;

  return (
    <div className="space-y-5" data-testid="admin-quotes-tab">
      <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-3 text-sm flex items-start gap-2">
        <FileText className="w-4 h-4 mt-0.5 text-emerald-700" />
        <div>
          <strong>Offertes — Blok E+F.</strong> Maak test-offertes voor pilots, bekijk status,
          en zie waar een herinnering nodig is. <em>Geen mail, geen provider-outreach, geen Stripe.</em>
          De knop <strong>Stuur herinnering</strong> staat klaar voor Blok D maar is nog uitgeschakeld.
        </div>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-6 gap-2">
        <KpiCard label="Totaal"          value={kTotal}    testid="admin-quotes-kpi-total" />
        <KpiCard label="Wacht op klant"  value={kWaiting}  testid="admin-quotes-kpi-waiting" tone="amber" />
        <KpiCard label="Goedgekeurd"     value={kApproved} testid="admin-quotes-kpi-approved" tone="emerald" />
        <KpiCard label="Afgewezen"       value={kRejected} testid="admin-quotes-kpi-rejected" tone="rose" />
        <KpiCard label="Reminder nodig"  value={kReminder} testid="admin-quotes-kpi-reminder" tone="amber" />
        <KpiCard label="Test-offertes"   value={kTest}     testid="admin-quotes-kpi-test" tone="blue" />
      </div>

      {/* Actions */}
      <div className="flex flex-wrap items-center gap-2">
        <button onClick={() => setShowForm(s => !s)}
          className="inline-flex items-center gap-1.5 px-3 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-md text-sm font-medium"
          data-testid="admin-quotes-new-btn">
          <Plus className="w-4 h-4" /> {showForm ? 'Verberg formulier' : 'Nieuwe test-offerte'}
        </button>
        <button onClick={load} disabled={loading}
          className="inline-flex items-center gap-1.5 px-3 py-2 bg-white border border-border rounded-md text-sm disabled:opacity-50"
          data-testid="admin-quotes-refresh-btn">
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} /> Vernieuwen
        </button>

        <div className="ml-auto flex flex-wrap items-center gap-1" data-testid="admin-quotes-filters">
          {FILTERS.map(f => (
            <button key={f.id} onClick={() => setFilter(f.id)}
              className={`px-2.5 py-1 text-xs rounded-full border transition-colors ${
                filter === f.id
                  ? 'bg-emerald-600 text-white border-emerald-600'
                  : 'bg-white text-foreground border-border hover:border-emerald-300'
              }`}
              data-testid={`admin-quotes-filter-${f.id}`}>
              {f.label}
            </button>
          ))}
        </div>
      </div>

      {showForm && (
        <CreateForm token={token} pilots={pilots}
          onCreated={onCreated} onClose={() => setShowForm(false)} />
      )}

      {/* Table */}
      <div className="bg-white border border-border rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="admin-quotes-table">
            <thead className="bg-slate-50 text-left text-[11px] uppercase tracking-wider text-muted-foreground">
              <tr>
                <th className="py-2 px-3">Offerte</th>
                <th className="py-2 px-3">Bedrag</th>
                <th className="py-2 px-3">Status</th>
                <th className="py-2 px-3">Tijdslijn</th>
                <th className="py-2 px-3 min-w-[14rem]">Beheer</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={5} className="py-8 text-center text-muted-foreground">Laden…</td></tr>
              )}
              {!loading && filtered.length === 0 && (
                <tr>
                  <td colSpan={5} className="py-10 text-center" data-testid="admin-quotes-empty">
                    <FileText className="w-8 h-8 mx-auto text-slate-300 mb-2" />
                    <div className="text-sm text-muted-foreground">
                      {filter === 'all'
                        ? 'Nog geen offertes aangemaakt. Klik op "Nieuwe test-offerte".'
                        : 'Geen offertes in dit filter.'}
                    </div>
                  </td>
                </tr>
              )}
              {!loading && filtered.map(q => (
                <QuoteRow key={q.quote_id} quote={q} token={token}
                  onChanged={onChanged} onPreview={setPreviewQuote} />
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Footer: explicit safety reassurance */}
      <div className="text-[11px] text-muted-foreground flex flex-wrap items-center gap-3 pt-1"
        data-testid="admin-quotes-safety-footer">
        <span className="inline-flex items-center gap-1"><CheckCircle2 className="w-3 h-3 text-emerald-600" /> Geen klantmail</span>
        <span className="inline-flex items-center gap-1"><CheckCircle2 className="w-3 h-3 text-emerald-600" /> Geen provider-outreach</span>
        <span className="inline-flex items-center gap-1"><CheckCircle2 className="w-3 h-3 text-emerald-600" /> Geen Stripe / Ads / live-control</span>
        <span className="inline-flex items-center gap-1"><Clock className="w-3 h-3" /> Echte mail wacht op Blok D</span>
      </div>

      {previewQuote && (
        <MailPreviewModal quote={previewQuote} token={token}
          onClose={() => setPreviewQuote(null)} />
      )}
    </div>
  );
}

// ---------- Mail preview modal (Blok D voorwerk · dry-run) -----------------
function MailPreviewModal({ quote, token, onClose }) {
  const initialType = suggestedMailType(quote) || 'quote_available';
  const [mailType, setMailType] = useState(initialType);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const fetchPreview = useCallback(async (type) => {
    setLoading(true); setError('');
    try {
      const r = await axios.get(
        `${API}/admin/quotes/${quote.quote_id}/mail-preview?type=${type}`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setData(r.data);
    } catch (e) {
      setError(e?.response?.data?.detail?.error
        || e?.response?.data?.detail || e.message);
      setData(null);
    } finally { setLoading(false); }
  }, [quote.quote_id, token]);

  useEffect(() => { fetchPreview(mailType); }, [mailType, fetchPreview]);

  const onBackdrop = (e) => { if (e.target === e.currentTarget) onClose(); };
  useEffect(() => {
    const h = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, [onClose]);

  const TYPES = [
    { id: 'quote_available', label: 'Offerte beschikbaar' },
    { id: 'quote_waiting_customer_reminder', label: 'Herinnering' },
    { id: 'quote_approved_confirmation', label: 'Bevestiging goedkeuring' },
    { id: 'quote_rejected_confirmation', label: 'Bevestiging afwijzing' },
  ];

  return (
    <div onClick={onBackdrop}
      className="fixed inset-0 z-50 bg-black/40 flex items-end sm:items-center sm:justify-center p-0 sm:p-4"
      data-testid="admin-quote-mail-preview-modal">
      <div className="bg-white w-full sm:max-w-2xl sm:rounded-2xl rounded-t-2xl shadow-xl max-h-[92vh] overflow-y-auto"
        role="dialog" aria-modal="true">
        {/* Header */}
        <div className="flex items-start justify-between gap-3 p-4 border-b border-border sticky top-0 bg-white">
          <div>
            <div className="flex items-center gap-2">
              <Mail className="w-4 h-4 text-emerald-700" />
              <h3 className="font-semibold text-base">Mailvoorbeeld</h3>
              <span className="inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full bg-amber-100 text-amber-900 border border-amber-200"
                data-testid="admin-quote-mail-dryrun-badge">
                Dry-run · niet verzonden
              </span>
            </div>
            <div className="text-xs text-muted-foreground mt-1 truncate">
              Offerte: <strong>{quote.title}</strong>
            </div>
          </div>
          <button onClick={onClose}
            className="p-1 hover:bg-slate-100 rounded-md"
            data-testid="admin-quote-mail-preview-close"
            aria-label="Sluiten">
            <XIcon className="w-4 h-4" />
          </button>
        </div>

        {/* Mail-type switcher */}
        <div className="p-4 border-b border-border space-y-2">
          <div className="text-xs text-muted-foreground">Mailtype</div>
          <div className="flex flex-wrap gap-1" data-testid="admin-quote-mail-types">
            {TYPES.map(t => (
              <button key={t.id} onClick={() => setMailType(t.id)}
                className={`px-2.5 py-1 text-xs rounded-full border transition-colors ${
                  mailType === t.id
                    ? 'bg-emerald-600 text-white border-emerald-600'
                    : 'bg-white text-foreground border-border hover:border-emerald-300'
                }`}
                data-testid={`admin-quote-mail-type-${t.id}`}>
                {t.label}
              </button>
            ))}
          </div>
        </div>

        {/* Body */}
        <div className="p-4 space-y-3">
          {loading && (
            <div className="text-sm text-muted-foreground py-6 text-center">Laden…</div>
          )}
          {!loading && error && (
            <div className="text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded-md p-2">
              {error}
            </div>
          )}
          {!loading && data && !data.preview_available && (
            <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 text-sm"
              data-testid="admin-quote-mail-preview-unavailable">
              <div className="font-medium mb-1">{data.mail_type_label}</div>
              <div className="text-muted-foreground">{data.message}</div>
              <div className="text-xs text-muted-foreground mt-2">
                Huidige status: <strong>{data.current_status_label}</strong>
              </div>
            </div>
          )}
          {!loading && data && data.preview_available && (
            <>
              <div className="grid grid-cols-1 sm:grid-cols-[6rem_1fr] gap-x-3 gap-y-1 text-sm">
                <div className="text-muted-foreground">Aan:</div>
                <div data-testid="admin-quote-mail-recipient">
                  {data.to_label}
                  {data.has_email
                    ? <span className="text-muted-foreground ml-1">&lt;{data.to_email_masked}&gt;</span>
                    : <span className="text-amber-700 ml-1">— Ontvanger nog onbekend</span>
                  }
                </div>
                <div className="text-muted-foreground">Onderwerp:</div>
                <div className="font-medium" data-testid="admin-quote-mail-subject">
                  {data.subject}
                </div>
              </div>

              <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 text-sm whitespace-pre-line leading-relaxed"
                data-testid="admin-quote-mail-body">
                {data.body_text}
              </div>

              {data.cta_label && data.cta_url && (
                <div className="flex items-center gap-2 text-sm pt-1">
                  <span className="text-muted-foreground">CTA:</span>
                  <span className="inline-block px-3 py-1.5 rounded-md bg-emerald-600 text-white text-xs"
                    data-testid="admin-quote-mail-cta-label">
                    {data.cta_label}
                  </span>
                  <span className="text-xs text-muted-foreground truncate">
                    → {data.cta_url}
                  </span>
                </div>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-border bg-slate-50 flex flex-wrap items-center justify-between gap-2">
          <div className="text-[11px] text-muted-foreground"
            data-testid="admin-quote-mail-dryrun-note">
            Dry-run · Geen Resend · Geen verzending · Geen mailstatus-mutatie.
          </div>
          <div className="flex gap-2">
            <button
              type="button"
              disabled
              title="Beschikbaar na Blok D mailactivatie"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs bg-slate-100 text-slate-500 border border-border cursor-not-allowed"
              data-testid="admin-quote-mail-send-real-disabled"
              aria-disabled="true"
            >
              <Mail className="w-3 h-3" /> Echte mail versturen
              <span className="text-[9px] uppercase opacity-70">na Blok D</span>
            </button>
            <button onClick={onClose}
              className="px-3 py-1.5 rounded-md text-xs bg-white border border-border hover:border-slate-400"
              data-testid="admin-quote-mail-preview-close-btn">
              Sluiten
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
